/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { LoadingService, LogService, ModalService, RbacService, UserService } from '../../../../../core/services';
import { NamespaceService } from '../../../shared/services/utils';
import { NamespacesApiService } from '../../../shared/http';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-namespace-list',
    templateUrl: './namespaces.component.html'
})

export class NamespacesComponent extends BaseComponent {
    /**
     * Table row data.
     */
    public tableRows: any[] = [];

    /**
     * Search term for searching the services table, also used with the search query parameter in the url.
     */
    public searchTerm: string = '';

    /**
     * Store the user's ability to edit.
     */
    public userCanEdit: boolean = false;

    /**
     * Store the current organisation ID for use in the API calls.
     */
    private readonly organisationId: string;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    constructor(
        public modalService: ModalService,
        private loadingService: LoadingService,
        private logService: LogService,
        private namespaceApiService: NamespacesApiService,
        private namespaceService: NamespaceService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService
    ) {
        super();

        this.organisationId = this.userService.getUser('organisationId');

        this.setUpPageData();
        this.setupUserValues();
        this.registerButtons();
    }

    /**
     * Add or edit namespace.
     * @param id
     */
    public addEditNamespace(id?: any): void {
        const url = id ? 'edit/' + id : 'add';
        this.router.navigate([url], { relativeTo: this.route }).then().catch();
    }

    /**
     * Confirm delete namespace.
     * @param id
     */
    public confirmDeleteNamespace(id: any): void {
        const confirmationObject = {
            title: 'namespace_delete',
            message: 'namespace_delete_message',
            callBack: this.deleteNamespace.bind(this, id),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete a name from the namespaces store.
     * @param id
     * @private
     */
    private deleteNamespace(id: string): Observable<any> {
        return new Observable((observer) => {
            this.namespaceApiService.delete(this.organisationId, id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('deleteNamespace', error.message);
                        observer.error(error);
                        observer.complete();
                    },
                    complete: (): void => {
                        this.loadingService.loadingActionDotsEmitter.next(false);
                    }
                });
        });
    }

    /**
     * Set up user values.
     * @private
     */
    private setupUserValues(): void {
        const user = this.userService.getUser();
        this.userCanEdit = user.permissions && (user.permissions < 4 && user.permissions !== 0);
    }

    /**
     * Update page data.
     * @private
     */
    private setUpPageData(): void {
        this.namespaceApiService.getAll(this.organisationId)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.namespaceService.namespaces = data;
                    this.populateTableRows();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('getUpdatedNamespacesList', error.message);
                }
            });
    }

    /**
     * Populate tables rows.
     * @private
     */
    private populateTableRows(): void {
        this.tableRows = [];
        for (const namespace of this.namespaceService.namespaces) {
            this.tableRows.push({ name: namespace.value, id: namespace.key });
        }
    }

    /**
     * Register button permissions.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'namespace-list-add': [
                { path: '/api/v1/organisations/:orgid/namespaces', methods: ['POST'] }
            ],
            'namespace-list-edit': [
                { path: '/api/v1/organisations/:orgid/namespaces/:id', methods: ['PATCH'] }
            ],
            'namespace-list-delete': [
                { path: '/api/v1/organisations/:orgid/namespaces/:id', methods: ['DELETE'] }
            ]
        };
        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
